import * as THREE from 'three';
import { Capabilities } from '@/core/Capabilities';
import { isDebug, settings } from '@/config';
import { WaterComplex } from './WaterComplex';
import { WaterSimple } from './WaterSimple';
import type { TickState, Updatable } from '@/types';

export interface WaterFXDeps {
  source: THREE.Mesh;
  renderer: THREE.WebGLRenderer;
  normalMap0?: THREE.Texture;
  normalMap1?: THREE.Texture;
}

/**
 * A water pool surface. Two implementations, chosen ONCE at construction from the boot-time
 * device probe (2026-09-04, user: "recreate water shaders specifically for low end devices —
 * keep this one for when we detect a GPU or a nice CPU; serve low-end devices the optimized
 * water shader"):
 *
 * - Capable desktops → `WaterComplex`: Water2-style real-time planar Reflection — whatever's
 *   actually in the scene (sky, clouds, architecture) is mirrored into the pool every frame,
 *   distorted by two scrolling normal maps, with a proper Fresnel falloff.
 * - Phones / software WebGL / LOW-tier machines → `WaterSimple`: a fully standalone cheap
 *   surface (fake sky mirror, same ripple + fresnel + glint character). The complex class is
 *   never constructed there, so none of its Reflector/nested-render machinery exists to fail.
 *   The choice is deliberately for the SESSION: a phone that happens to hit 60fps must still
 *   never be handed the complex shader by the adaptive monitor.
 *
 * Previously also drove a pointer-hover ripple/fluid-sim distortion (`RippleFX`/`FluidFX`);
 * removed 2026-07-02 per the user ("remove that hovering thing from the water") in favour of
 * just the calm realistic reflection.
 */
export class WaterFX implements Updatable {
  /** The rendered surface, whichever implementation was chosen. */
  readonly mesh: THREE.Mesh;
  /** The reflective implementation — null on low-end devices (see class note). `World` only
   *  wires the peers/reflection-pass hooks when this exists. */
  readonly complex: WaterComplex | null = null;
  private readonly simple: WaterSimple | null = null;

  // The complex shader always samples `u_fx` (no #ifdef gate around it) even with no hover
  // FX, so a static black 1x1 texture keeps its distortion/tint contribution at zero.
  private readonly blackTex: THREE.DataTexture | null = null;

  /** DEVICE GATE (2026-09-04). Not tier-alone: a modern phone GPU (Adreno 7xx / Mali-G)
   *  classifies as MEDIUM tier, so gating on `tier === 'low'` still served phones the complex
   *  planar-reflection water — the exact breakage the user kept hitting. Any mobile device,
   *  any software renderer, and anything booting on the LOW tier gets `WaterSimple`. */
  static shouldUseSimple(): boolean {
    // `?simplewater` previews the low-end surface on any machine (testing without a phone).
    if (new URLSearchParams(window.location.search).has('simplewater')) return true;
    const caps = Capabilities.current;
    if (!caps) return false;
    return caps.mobile || !caps.hardwareAccelerated || caps.tier === 'low';
  }

  constructor(deps: WaterFXDeps) {
    const cfg = settings.water;

    deps.source.updateWorldMatrix(true, false);

    // Reuse the exported pool outline. GOTCHA: bake the source's FULL world matrix
    // (position + rotation + SCALE) into the geometry directly — an earlier version
    // only re-derived a world-space CENTER (via Box3) and cloned the RAW local
    // geometry, silently dropping any scale authored on the node. This GLB's
    // `Water_Mesh` node carries a ~1.45x scale, so the pool rendered at its raw
    // unscaled size instead of the authored one. Then reorient into Water's
    // authoring space (XY,+Z) on top of the now-world-space geometry.
    const geometry = deps.source.geometry.clone();
    geometry.applyMatrix4(deps.source.matrixWorld);

    // MIRROR-PLANE FIX (2026-07-20, user: "how a mirror works — whatever is on top of it has to
    // reflect exactly the same; right now the reflection is not correct").
    //
    // Baking the world matrix into the vertices leaves the MESH transform at the origin — and
    // the planar `Reflector` derives its mirror plane from the mesh's `matrixWorld`, not from
    // the vertices. So every pool was mirroring about y = 0 while its actual surface sat at its
    // authored height (≈0.8 for the Scene-1 pool, far higher for Scenes 3/4): all reflections
    // were vertically offset by TWICE the surface height and never lined up with the objects
    // above them. Lift the surface height OUT of the geometry and carry it on the mesh's
    // position instead — the rendered pool is identical, but the reflector (and the projective
    // `textureMatrix`, which reads the same `matrixWorld`) now mirrors about the true surface.
    // (WaterSimple has no reflector, but keeping the identical transform means the two
    // implementations are pixel-for-pixel interchangeable in the scene.)
    geometry.computeBoundingBox();
    const surfaceY = geometry.boundingBox!.max.y;
    geometry.translate(0, -surfaceY, 0);

    geometry.rotateX(Math.PI / 2);
    this.ensureUV(geometry);

    if (WaterFX.shouldUseSimple()) {
      this.simple = new WaterSimple(geometry, {
        normalMap0: deps.normalMap0,
        normalMap1: deps.normalMap1,
      });
      this.mesh = this.simple;
    } else {
      this.complex = new WaterComplex(geometry, {
        color: cfg.color,
        scale: cfg.scale,
        flowDirection: new THREE.Vector2(cfg.flowDirection[0], cfg.flowDirection[1]),
        flowSpeed: cfg.flowSpeed,
        textureWidth: cfg.dimensions,
        textureHeight: cfg.dimensions,
        normalMap0: deps.normalMap0,
        normalMap1: deps.normalMap1,
        reflectivity: cfg.reflectivity,
      });
      this.mesh = this.complex;

      this.blackTex = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
      this.blackTex.needsUpdate = true;
      this.complex.setFxTexture(this.blackTex);

      // Land every derived/tunable setting (IOR-driven reflectivity, deep colour, sun…) once
      // now; per-frame re-sync below is debug-only.
      this.syncWaterUniforms(settings.water);
    }

    // The geometry is world-space in XZ (baked above) with the surface height extracted; the
    // wrapper mesh carries the un-rotation AND that height, so `matrixWorld` describes the true
    // mirror plane (see the fix note above).
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.y = surfaceY;
    this.mesh.renderOrder = 1;
  }

  update(tick: TickState): void {
    if (this.simple) {
      this.simple.update(tick.delta);
      return;
    }
    // LOW-END CPU PASS (2026-09-01): this re-parses several hex-string colours into uniforms
    // every frame, per pool — pure waste unless the debug GUI is live-tuning them. Settings are
    // static in production, so sync once at construction and only keep the per-frame push where
    // the panel exists to change things.
    if (isDebug) this.syncWaterUniforms(settings.water);
  }

  /** Push live-tunable settings into the complex water's uniforms (instant debug GUI). */
  private syncWaterUniforms(cfg: typeof settings.water): void {
    if (!this.complex) return;
    const u = this.complex.material.uniforms as Record<string, THREE.IUniform>;
    // Physical reflectance from IOR (Schlick F0), floored by the artistic `mirror` control —
    // the reference pool is a near-mirror even looking down, which physical F0 (0.02) can never
    // give. Fresnel still ramps to a full mirror at grazing angles on top of this base.
    const f = (cfg.ior - 1) / (cfg.ior + 1);
    cfg.reflectivity = Math.max(f * f, cfg.mirror);
    u['reflectivity']!.value = cfg.reflectivity;
    (u['color']!.value as THREE.Color).set(cfg.color);
    (u['deepColor']!.value as THREE.Color).set(cfg.deepColor);
    u['distortionScale']!.value = cfg.distortion;
    (u['uSunColor']!.value as THREE.Color).set(cfg.sunColor);
    (u['uSunDir']!.value as THREE.Vector3).set(
      cfg.sunDirection[0],
      cfg.sunDirection[1],
      cfg.sunDirection[2],
    );
    u['uSunShininess']!.value = cfg.sunShininess;
    u['uSunIntensity']!.value = cfg.sunIntensity;
    (u['config']!.value as THREE.Vector4).w = cfg.scale;
    this.complex.flowSpeed = cfg.flowSpeed;
    const flow = u['flowDirection'];
    if (flow) (flow.value as THREE.Vector2).set(cfg.flowDirection[0], cfg.flowDirection[1]);
  }

  /** Adaptive quality — complex water only: shrink the reflection RT and, on the LOW tier,
   *  switch to its non-reflective fallback look (zero nested renders, never black, never
   *  flickers) — see `WaterComplex.setLiveReflections`. `WaterSimple` has nothing to adapt
   *  and must never be swapped back (the device gate is for the session). */
  setQuality(tier: 'high' | 'medium' | 'low'): void {
    if (!this.complex) return;
    const profile = settings.performance.profiles[tier];
    this.complex.setReflectionResolution(profile.waterRes);
    this.complex.setLiveReflections(profile.reflections);
  }

  /** Planar UVs over the geometry's XY bounds, only if the source mesh lacked them. */
  private ensureUV(geometry: THREE.BufferGeometry): void {
    if (geometry.getAttribute('uv')) return;
    const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
    geometry.computeBoundingBox();
    const box = geometry.boundingBox!;
    const sx = box.max.x - box.min.x || 1;
    const sy = box.max.y - box.min.y || 1;
    const uv = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i += 1) {
      uv[i * 2] = (pos.getX(i) - box.min.x) / sx;
      uv[i * 2 + 1] = (pos.getY(i) - box.min.y) / sy;
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  }

  dispose(): void {
    this.complex?.dispose();
    this.simple?.dispose();
    this.blackTex?.dispose();
  }
}
